//dependicies
import { format, isAfter, parseISO } from "date-fns";
import React from "react";

import { Avatar } from "@performyard/py-components";

//components
import Button from "../../../../../../components/Button";
import Link from "../../../../../../components/Link";
import { ScrollableColumn } from "../../../../../../components/TableLite/TableLiteScrollable/TableLiteScrollable";
import ClampedTableText from "../../../../../../components/TableLite/components/ClampedTableText";

//utils
import get from "../../../../../../utils/GetNested";
import toEmployeeUrl from "../../../../../../utils/URLs/Employee";
import { arrayIncludes, findAuthorOrSigner } from "../../../utils";

//css
import css from "../../../UserReviews.module.scss";

export const FormsINeedToDoTableCols = (userId: string): ScrollableColumn[] => {
	return [
		{
			id: "view",
			label: "",
			width: 96,
			isSortable: false,
			getter: (report) => (
				<div>
					<Button href={`/review/${report._id}/`} color="blue">
						Open Form
					</Button>
				</div>
			),
		},
		{
			id: "subject",
			label: "Subject",
			width: 150,
			isSortable: false,
			getter: (report) => {
				const subjectID = get(report, ["subject"], null);
				const subjectName = get(report, ["subject_name"], null);

				return (
					<div className={css.userBox}>
						<Avatar
							size={20}
							dangerouslySetClassName={css.userAvatar}
							id={subjectID}
							alt={`${subjectName}'s avatar`}
						/>
						<div className={css.userName}>
							<Link href={toEmployeeUrl(subjectID)} forceHardReload={true}>
								{subjectName}
							</Link>
						</div>
					</div>
				);
			},
		},
		{
			id: "form",
			label: "Form",
			isSortable: false,
			width: 280,
			getter: (report) => {
				const formName = get(report, ["form_name"], null);
				return <ClampedTableText>{formName}</ClampedTableText>;
			},
		},
		{
			id: "authorsDueDate",
			label: "Author's Due Date",
			sortName: "authors_due_date",
			width: 150,
			getter: (report) => {
				const date = get(report, ["authors_due_date"], null);

				if (date) {
					const parsedDate = parseISO(date);
					return format(parsedDate, "MMM d, yyyy hh:mm a");
				}

				return null;
			},
		},
		{
			id: "signersDueDate",
			label: "Signer's Due Date",
			sortName: "signers_due_date",
			width: 150,
			getter: (report) => {
				const date = get(report, ["signers_due_date"], null);
				const signers = get(report, ["signers"], []);

				if (date && signers?.length > 0 && !(signers?.length === 1 && signers[0] === null)) {
					const parsedDate = parseISO(date);

					return format(parsedDate, "MMM d, yyyy hh:mm a");
				}

				return null;
			},
		},
		{
			id: "status",
			width: 184,
			label: "Status",
			isSortable: false,
			getter: (report) => {
				const submit = get(report, ["submit"], false);
				const startDate = get(report, ["start_date"], null);
				const completed = get(report, ["completed"], false);
				const redacted = get(report, ["redacted"], false);
				const authorTurn = get(report, ["author_turn"], 0);
				const signerTurn = get(report, ["signer_turn"], 0);
				const authorIDs = get(report, ["authors"], []);
				const authors = get(report, ["author_objects"], []);
				const signers = get(report, ["signer_objects"], []);
				const pastAuthors = get(report, ["past_authors"], null);
				const pastSigners = get(report, ["past_signers"], null);
				// Below are the current author and signer but to prevent conflict of variable names
				const currentSigner = get(report, ["signer"], null);
				const currentAuthor = get(report, ["author"], null);
				let status = "";

				if (completed) {
					return <ClampedTableText>Completed</ClampedTableText>;
				}

				const parsedDate = parseISO(startDate);
				if (startDate && isAfter(parsedDate, new Date())) {
					status = `Starting ${format(parsedDate, "MMM d, yyyy h:mm a")}`;
					return <ClampedTableText>{status}</ClampedTableText>;
				}

				if (
					!submit &&
					(authorTurn < authorIDs?.length ||
						arrayIncludes(
							authors.map((author) => author._id),
							"_redacted",
							0
						))
				) {
					// check if author signed but not submitted yet
					if (arrayIncludes(pastAuthors, userId, 0)) {
						const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
						status = `Awaiting submission by ${
							currentAuthorInfo?.name || "Unassigned"
						}(Filled out the form)`;
					}

					// For users Forms I Need To Do
					else if (
						arrayIncludes(authorIDs, userId, authorTurn) ||
						arrayIncludes(
							authors.map((author) => author._id),
							"_redacted",
							0
						)
					) {
						if (currentAuthor !== userId) {
							const currentAuthorInfo = findAuthorOrSigner(
								get(report, ["author_objects"]),
								currentAuthor
							);
							status = `Awaiting submission by ${
								redacted ? "Anonymous" : currentAuthorInfo?.name || "Unassigned"
							} (Fill out the form)`;
						} else {
							status = "Fill out the form";
						}
					} else {
						try {
							const currentAuthorInfo = findAuthorOrSigner(
								get(report, ["author_objects"]),
								currentAuthor
							);
							status = `Awaiting submission by ${
								redacted ? "Anonymous" : currentAuthorInfo.name
							} (Sign off the form)`;
						} catch (error) {
							status = "Awaiting submission by Unassigned (Sign off the form)";
						}
					}
				} else if (signerTurn <= signers?.length) {
					if (arrayIncludes(pastAuthors, userId, 0)) {
						if (currentSigner === userId) {
							// if the user is the current signer, and they were an author, and all of the authors have authored the form
							status = "Sign off the form (Filled out the form)";
						} else {
							const currentSignerInfo = findAuthorOrSigner(
								get(report, ["signer_objects"]),
								currentSigner
							);
							status = `Awaiting sign off by ${
								currentSignerInfo?.name || "Unassigned"
							} (Filled out the form)`;
						}
					} else if (arrayIncludes(pastSigners, userId, 0)) {
						const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
						status = `Awaiting sign off by ${
							currentSignerInfo?.name || "Unassigned"
						} (Signed off the form)`;
					} else if (currentSigner !== userId) {
						const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
						status = `Awaiting sign off by ${currentSignerInfo?.name || "Unassigned"}`;
					} else {
						status = "Sign off the form";
					}
				}

				return <ClampedTableText>{status}</ClampedTableText>;
			},
		},
	];
};

export default FormsINeedToDoTableCols;
